import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'

import { MsalProvider } from '@azure/msal-react'
import GlobalStyles from '@mui/material/GlobalStyles'
import { ThemeProvider } from '@mui/material/styles'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { Toaster } from 'react-hot-toast'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import { UserContextProvider } from '@context/userContext'
import { SSOInstance } from '@lib/microsoft-authentication-library'
import { theme } from './theme'

const App = lazy(() => import('./App'))
const ErrorBoundary = lazy(() => import('./pages/ErrorBoundary'))
const Login = lazy(() => import('./pages/Login'))
const PipelineQueue = lazy(() => import('./pages/PipelineQueue'))
const PipelineReview = lazy(() => import('./pages/PipelineReview'))
const DealsQueue = lazy(() => import('./pages/DealsQueue'))
const DealReview = lazy(() => import('./pages/DealReview'))
const PRReview = lazy(() => import('./pages/PRReview'))

Sentry.init({
  environment: process.env.REACT_APP_SENTRY_ENV,
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [new BrowserTracing()],
  tracesSampleRate: +process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
})

const root = ReactDOM.createRoot(document.getElementById('root'))
root.render(
  <MsalProvider instance={SSOInstance}>
    <UserContextProvider>
      <ErrorBoundary>
        <Suspense>
          <ThemeProvider theme={theme}>
            <BrowserRouter>
              <QueryParamProvider adapter={ReactRouter6Adapter}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route element={<App />}>
                    {/* Pipelines */}
                    <Route path="/pipelines/queue" element={<PipelineQueue />} />
                    <Route path="/pipelines/url/:pipelineUrlId/review/" element={<PipelineReview />} />

                    {/* Deals */}
                    <Route path="/deals/queue" element={<DealsQueue />} />
                    <Route path="/deals/pr-review/:code" element={<PRReview />} />
                    <Route path="/deals/review/:dealEventCode" element={<DealReview />} />
                    <Route path="/deals/review/deal/:reviewDealCode" element={<DealReview />} />

                    <Route path="*" element={<Navigate to="/pipelines/queue" replace />} />
                  </Route>
                </Routes>
              </QueryParamProvider>
            </BrowserRouter>
          </ThemeProvider>
        </Suspense>
        <Toaster />
      </ErrorBoundary>
      <GlobalStyles
        styles={{
          'html, body, #root': { height: '100%', margin: 0 },
          '[aria-live="polite"]': { margin: 0 },
        }}
      />
    </UserContextProvider>
  </MsalProvider>,
)
